import React, {FC, useEffect, useState, useCallback, useMemo, useRef} from 'react'
import {Col, Form, Row, OverlayTrigger, Tooltip, Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {ReactSortable} from 'react-sortablejs'
import {useI18n} from '../../../../../Utils/_helpers/useI18n'
import {
  FIELD_OPTIONS,
  REQUIRED_VALUE,
  OPTIONAL_VALUE,
  MAX_FIELDS_ALLOWED,
  MIN_FIELDS_ALLOWED,
  FIELD_PLACEHOLDER_TO_KEY_MAP,
  WALLET_CARD_FORM_SETTINGS,
} from '../../../../../Utils/Constant/WalletCard'
import {SettingFormConnectProps, FormConnectSettingsData, FormSetting} from '../../type'
import InputPromoAction from '../../../../../Components/InputPromoAction'
import InputVehicleTypeOption from '../../../../../Components/InputVehicleTypeOption'
import Select, {components, MenuListProps, StylesConfig} from 'react-select'
import {useDispatch, useSelector} from 'react-redux'
import {
  getAllTemplateWithoutPagination,
  getTemplateListWithoutPagination,
} from '../../../MyTemplate/CreateScratch/CreateScratchSlices/CreateScratchSlices'
import './SettingFormConnect.scss'
import CreateScratchV2 from '../../../MyTemplate/CreateScratch/CreateScratchV2'
import {TemplateMessage} from '../../../MyTemplate/CreateScratch/Template'
import {AuthenticateUser} from '../../../../../Utils/_helpers/RoleAndPermission'
import {useSenderId} from '../../../../UserPages/Flow/WorkFlow/FlowCanvas/hooks/useSenderId'
import {findMatchingArrayName} from '../../../../../Utils/_helpers'
import {REGION} from '../../../../../Utils/Constant/ConstantForWebsite'
import clsx from 'clsx'
import {Elements} from '@stripe/react-stripe-js'
import StripeJSProvider from '../../../../../Utils/StripeJSProvider'
import CreateBusinessNamePopup from '../../../accounts/BusinessName/Modal/CreateBusinessNamePopup'
import CreateCutomNumber from '../../../accounts/CustomNumber/Modal/CreateCutomNumber'
import VirtualNumbersModal from '../../../accounts/DedicatedNumber/VirtualNumbersModal'
import {SelectOption} from '../../../../../types'

const SettingFormConnect: FC<SettingFormConnectProps> = ({
  cardData,
  handleChange,
  handleBlur,
  touched,
  errors,
  MAX_LENGTH,
  disableInput,
  isExpanded = true,
  onExpand,
  isCompleted = false,
  setIsCompleted,
  isLoading = false,
  promoSettingIds,
  setPromoSettingIds,
  vehicleTypeOptionIds,
  setVehicleTypeOptionIds,
  formFields: propFormFields,
  setFormFields: propSetFormFields,
  promoActionList,
  onFormConnectDataChange,
  gateUntilOpened,
}) => {
  const {t} = useI18n()
  const dispatch = useDispatch()
  const [openTemplatePopup, setOpenTemplatePopup] = useState(false)
  const templates: any[] = useSelector(getTemplateListWithoutPagination) || []

  // Fetch templates on mount
  useEffect(() => {
    dispatch(getAllTemplateWithoutPagination({type: 'SMS'}))
  }, [dispatch])

  // Options for react-select
  const templateOptions = useMemo(() => {
    const defaultOption = {
      value: null,
      label: t('WALLET_CARD.FORM.SELECT_TEMPLATE') || 'Select Template',
    }
    const aiWelcomeMessageOption = {
      value: 'ai-welcome-message',
      label: t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.AI_WELCOME_MESSAGE') || 'AI Welcome Message',
    }
    const mapped = templates.map((tpl) => ({
      value: tpl?._id,
      label: tpl?.name || '',
    }))
    return [defaultOption, aiWelcomeMessageOption, ...mapped]
  }, [templates, t])

  const [selectedTemplate, setSelectedTemplate] = useState<{value: string; label: string} | null>(
    null
  )

  // Delay minutes select (1,5,10,15,30,60) with default 0 when unset
  const delayOptions = useMemo(
    () => [1, 5, 10, 15, 30, 60].map((v) => ({value: v, label: `${v} min`})),
    []
  )
  const [selectedDelay, setSelectedDelay] = useState<{value: number; label: string} | null>({
    value: 1,
    label: '1 min',
  })

  // Custom styles for react-select to match project theme
  const selectStyles: StylesConfig<any, false> = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#f0ebff' : state.isFocused ? '#f8f5ff' : 'white',
      color: state.isSelected ? '#7950f3' : '#3F4254',
      cursor: 'pointer',
    }),
    control: (provided) => ({
      ...provided,
      borderRadius: '6px',
      borderColor: '#E4E6EF',
      minHeight: '40px',
      boxShadow: 'none',
      '&:hover': {borderColor: '#E4E6EF'},
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#3F4254',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  }

  // Compact styles for the small delay select
  const delaySelectStyles: StylesConfig<any, false> = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#ffffff' : state.isFocused ? '#f8f5ff' : 'white',
      color: state.isSelected ? '#7950f3' : '#3F4254',
      cursor: 'pointer',
      padding: 6,
    }),
    container: (provided) => ({
      ...provided,
      width: 'fit-content',
      minWidth: 90,
    }),
    control: (provided) => ({
      ...provided,
      width: 'fit-content',
      minHeight: '32px',
      borderRadius: '6px',
      borderColor: '#E4E6EF',
      backgroundColor: '#ffffff',
      boxShadow: 'none',
      paddingLeft: 8,
      paddingRight: 8,
      '&:hover': {borderColor: '#E4E6EF'},
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '2px 0',
      overflow: 'visible',
      flexWrap: 'nowrap',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      padding: 0,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#3F4254',
      maxWidth: 'none',
      overflow: 'visible',
      textOverflow: 'initial',
      whiteSpace: 'nowrap',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  }

  // Sync selectedTemplate when cardData or templates change
  useEffect(() => {
    if (!cardData?.template) {
      if (cardData.aiWelcomeMessage === true) {
        const aiOption = {
          value: 'ai-welcome-message',
          label: t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.AI_WELCOME_MESSAGE') || 'AI Welcome Message',
        }
        if (selectedTemplate?.value !== 'ai-welcome-message') {
          setSelectedTemplate(aiOption)
        }
      } else {
        if (selectedTemplate !== null) {
          setSelectedTemplate(null)
        }
      }
      return
    }
    const option = templates
      .map((tpl) => ({value: tpl?._id || tpl?.id || '', label: tpl?.name || ''}))
      .find((o) => o.value === cardData.template) || null
    if ((selectedTemplate?.value ?? null) !== (option?.value ?? null)) {
      setSelectedTemplate(option)
    }
  }, [cardData?.template, cardData?.aiWelcomeMessage, templates, t, selectedTemplate])

  // Sync selectedDelay when cardData changes
  useEffect(() => {
    const dm = Number((cardData as any)?.delayMinutes || 0)
    if (!dm) {
      setSelectedDelay({value: 1, label: '1 min'})
      handleChange({target: {name: 'delayMinutes', value: 1}})
    } else {
      setSelectedDelay({value: dm, label: `${dm} min`})
    }
  }, [cardData, handleChange])

  // Ensure empty value can clear selection
  const handleTemplateChange = (option: any) => {
    setSelectedTemplate(option)
    const value = option ? option.value : null
    if (value === null) {
      // Clear selection entirely
      handleChange({target: {name: 'aiWelcomeMessage', value: false}})
      handleChange({target: {name: 'template', value: ''}})
      return
    }
    if (value === 'ai-welcome-message') {
      // AI selected: enable AI, clear template
      handleChange({target: {name: 'aiWelcomeMessage', value: true}})
      handleChange({target: {name: 'template', value: ''}})
    } else {
      // Normal template selected: disable AI, set template
      handleChange({target: {name: 'aiWelcomeMessage', value: false}})
      handleChange({target: {name: 'template', value}})
    }
  }

  const handleSettingSenderChange = (e: SelectOption<string>) => {
    const value = e?.value
    const senderType = findMatchingArrayName(value, senderTypeData)

    setSelectedSender(value)
    setSelectedSenderType(senderType)

    handleChange({target: {name: 'sender', value}})
    handleChange({target: {name: 'senderType', value: senderType}})
  }

  const handleDelayChange = (option: any) => {
    const chosen = option || {value: 1, label: '1 min'}
    setSelectedDelay(chosen)
    const value = Number(chosen.value)
    handleChange({
      target: {
        name: 'delayMinutes',
        value,
      },
    })
  }

  const MenuList = (props: MenuListProps<any>) => {
    return (
      <div className='template-select'>
        <div className='template-select-content'>{props.children}</div>
        {AuthenticateUser('create-template') && (
          <div
            className='template-select-create-button d-flex align-items-center gap-2 justify-content-center cursor-pointer'
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setOpenTemplatePopup(true)
            }}
          >
            <i className='bi bi-plus-lg text-primary fw-bold'></i>
            <p className='text-primary fw-bold'>{t('WALLET_CARD.FORM.CREATE_NEW')}</p>
          </div>
        )}
      </div>
    )
  }

  const [isContentVisible, setIsContentVisible] = useState(isExpanded)
  const [hasOpenedOnce, setHasOpenedOnce] = useState<boolean>(isExpanded)

  const [templateVehicleTypeSelection, setTemplateVehicleTypeSelection] = useState<string[]>(
    vehicleTypeOptionIds ?? []
  )

  useEffect(() => {
    setTemplateVehicleTypeSelection(vehicleTypeOptionIds ?? [])
  }, [vehicleTypeOptionIds])

  const createAllFields = useMemo(() => {
    const allFields = FIELD_OPTIONS.map((option, index) => {
      const existingField = cardData.formSettings.find(
        (field) => field.key === FIELD_PLACEHOLDER_TO_KEY_MAP[option.value]
      )

      const isFullNameField = option.value === 'Full Name'

      if (existingField) {
        return {
          ...existingField,
          isDisplay: isFullNameField
            ? true
            : existingField.isDisplay !== undefined
            ? existingField.isDisplay
            : true,
          isRequired: isFullNameField ? true : existingField.isRequired,
        }
      } else {
        return {
          _id: `field_${option.value.replace(/\s+/g, '_').toLowerCase()}_${index}`,
          label: option.value,
          key: FIELD_PLACEHOLDER_TO_KEY_MAP[option.value],
          color: '',
          isRequired: isFullNameField ? true : false,
          order: index + 1,
          isDisplay: isFullNameField ? true : false,
        }
      }
    })

    return allFields
  }, [cardData.formSettings])

  const [localFormFields, setLocalFormFields] = useState<FormSetting[]>(() => createAllFields)

  const formFields = propFormFields || localFormFields

  const previousFormSettingsRef = useRef(cardData.formSettings)

  useEffect(() => {
    if (!propFormFields && previousFormSettingsRef.current !== cardData.formSettings) {
      previousFormSettingsRef.current = cardData.formSettings
      setLocalFormFields(createAllFields)
    }
  }, [cardData.formSettings, createAllFields, propFormFields])

  useEffect(() => {
    if (!setIsCompleted) return

    const visibleFields = formFields.filter((field) => field.isDisplay !== false)
    const hasValidForm = visibleFields.length >= MIN_FIELDS_ALLOWED

    if (hasValidForm !== isCompleted) {
      setIsCompleted(hasValidForm)
    }
  }, [promoSettingIds, formFields, setIsCompleted, isCompleted])

  useEffect(() => {
    setIsContentVisible(isExpanded)
    if (isExpanded) setHasOpenedOnce(true)
  }, [isExpanded])

  const toggleContent = () => {
    const newState = !isContentVisible
    setIsContentVisible(newState)
    if (newState && onExpand) {
      onExpand()
    }
    if (newState) setHasOpenedOnce(true)
  }

  const handlePromoActionChange = (selectedIds: string[]) => {
    if (setPromoSettingIds) {
      setPromoSettingIds(selectedIds)
    }

    handleChange({
      target: {
        name: 'promoSettings',
        value: selectedIds,
      },
    })
  }

  const updateFormField = useCallback(
    (id: string, field: keyof FormSetting, value: any) => {
      if (propSetFormFields && propFormFields) {
        const updatedFields = propFormFields.map((formField) => {
          if (formField._id === id) {
            return {...formField, [field]: value}
          }
          return formField
        })
        propSetFormFields(updatedFields)
      } else {
        setLocalFormFields((prevFields) =>
          prevFields.map((formField) => {
            if (formField._id === id) {
              return {...formField, [field]: value}
            }
            return formField
          })
        )
      }
    },
    [propSetFormFields, propFormFields]
  )

  const toggleFieldVisibility = useCallback(
    (id: string) => {
      if (propSetFormFields && propFormFields) {
        const field = propFormFields.find((f) => f._id === id)
        if (!field) return

        // Prevent disabling Full Name field
        if (field.label === WALLET_CARD_FORM_SETTINGS.FULL_NAME && field.isDisplay !== false) {
          toast.warning(t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.FULL_NAME_REQUIRED'))
          return
        }

        if (field.isDisplay !== false) {
          const visibleFieldsCount = propFormFields.filter((f) => f.isDisplay !== false).length

          if (visibleFieldsCount <= MIN_FIELDS_ALLOWED) {
            toast.warning(
              t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.NUMBER_FIELDS_REQUIRED', {
                number: MIN_FIELDS_ALLOWED,
              })
            )
            return
          }
        } else {
          const visibleFieldsCount = propFormFields.filter((f) => f.isDisplay !== false).length
          if (visibleFieldsCount >= MAX_FIELDS_ALLOWED) {
            toast.warning(t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.MAX_FIELDS_ALLOWED'))
            return
          }
        }

        const updatedFields = propFormFields.map((f) => {
          if (f._id === id) {
            return {...f, isDisplay: f.isDisplay === false ? true : false}
          }
          return f
        })

        propSetFormFields(updatedFields)
      } else {
        setLocalFormFields((prevFields) => {
          const field = prevFields.find((f) => f._id === id)
          if (!field) return prevFields

          // Prevent disabling Full Name field
          if (field.label === WALLET_CARD_FORM_SETTINGS.FULL_NAME && field.isDisplay !== false) {
            toast.warning(t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.FULL_NAME_REQUIRED'))
            return prevFields
          }

          if (field.isDisplay !== false) {
            const visibleFieldsCount = prevFields.filter((f) => f.isDisplay !== false).length

            if (visibleFieldsCount <= MIN_FIELDS_ALLOWED) {
              toast.warning(
                t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.NUMBER_FIELDS_REQUIRED', {
                  number: MIN_FIELDS_ALLOWED,
                })
              )
              return prevFields
            }
          } else {
            const visibleFieldsCount = prevFields.filter((f) => f.isDisplay !== false).length
            if (visibleFieldsCount >= MAX_FIELDS_ALLOWED) {
              toast.warning(t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.MAX_FIELDS_ALLOWED'))
              return prevFields
            }
          }

          return prevFields.map((f) => {
            if (f._id === id) {
              return {...f, isDisplay: f.isDisplay === false ? true : false}
            }
            return f
          })
        })
      }
    },
    [propSetFormFields, propFormFields, t]
  )

  // Prepare fields for sortable (add id property)
  const sortableFields = useMemo(() => {
    return formFields.map((field) => ({
      ...field,
      id: field._id, // ReactSortable requires 'id' property
    }))
  }, [formFields])

  // Handle drag and drop to reorder fields
  const handleSortableChange = useCallback(
    (sortedFields: FormSetting[]) => {
      // Update order based on new position
      const fieldsWithNewOrder = sortedFields.map((field, index) => ({
        ...field,
        order: index + 1,
      }))

      if (propSetFormFields) {
        propSetFormFields(fieldsWithNewOrder)
      } else {
        setLocalFormFields(fieldsWithNewOrder)
      }
    },
    [propSetFormFields]
  )

  const formConnectSettingsData = useMemo((): FormConnectSettingsData => {
    return {
      promoSettings: promoSettingIds || [],
      formSettings: formFields.filter((field) => field.isDisplay !== false),
    }
  }, [promoSettingIds, formFields])

  const previousFormConnectDataRef = useRef<FormConnectSettingsData>()

  useEffect(() => {
    if (onFormConnectDataChange) {
      const currentData = formConnectSettingsData

      if (
        !previousFormConnectDataRef.current ||
        JSON.stringify(previousFormConnectDataRef.current) !== JSON.stringify(currentData)
      ) {
        previousFormConnectDataRef.current = currentData
        onFormConnectDataChange(currentData)
      }
    }
  }, [onFormConnectDataChange, formConnectSettingsData])

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const handleModalSave = useCallback(
    (template: TemplateMessage) => {
      setOpenTemplatePopup(false)
      dispatch(getAllTemplateWithoutPagination())
    },
    [dispatch]
  )

  const {
    selectedSender,
    setSelectedSender,
    selectedSenderType,
    setSelectedSenderType,
    isDefaultRegion,
    userRegion,
    isShowCredit,
    dedicatedOptionMMS,
    dedicatedOptionSMS,
    businessNameOption,
    customNumberOption,
    senderTypeData,
    visible,
    setVisible,
    name,
    setName,
    customNumber,
    isAdmin,
    alphaID,
    setCustomNumber,
  } = useSenderId(cardData?.sender, cardData?.senderType)

  const MenuListComponent = (props: any) => {
    const {isVisible, ...rest} = props
    return (
      <components.MenuList {...rest}>
        {props.children}
        {rest?.options?.length === 1 && isAdmin === 'false' ? (
          <>
            {isShowCredit && [REGION.AU, REGION.US].includes(userRegion) && (
              <OverlayTrigger
                placement='top'
                container={typeof window !== 'undefined' ? document.body : undefined}
                containerPadding={20}
                overlay={
                  <Tooltip id='button-tooltip' className='high-z-index-tooltip'>
                    {t('SENDER_ID.CREATE_NEW_DEDICATED_NUMBER')}
                  </Tooltip>
                }
              >
                <div
                  className={clsx('senderid-menu-action', {'d-none': !isDefaultRegion})}
                  onClick={() => {
                    setVisible(true)
                  }}
                >
                  <i className='bi bi-plus-lg senderid-menu-action__icon'></i>
                  <p className='senderid-menu-action__label'>
                    {t('SENDER_ID.CREATE_NEW_DEDICATED_NUMBER')}
                  </p>
                </div>
              </OverlayTrigger>
            )}
            {isVisible && (
              <>
                {[REGION.AU, REGION.US, REGION.UK].includes(userRegion) && (
                  <OverlayTrigger
                    placement='top'
                    container={typeof window !== 'undefined' ? document.body : undefined}
                    containerPadding={20}
                    overlay={
                      <Tooltip id='button-tooltip' className='high-z-index-tooltip'>
                        {t('SENDER_ID.CREATE_NEW_CUSTOM_BUSINESS_NAME')}
                      </Tooltip>
                    }
                  >
                    <div
                      className={clsx('senderid-menu-action', {
                        'd-none': !isDefaultRegion && ![REGION.UK].includes(userRegion),
                      })}
                      onClick={() => {
                        setName(true)
                      }}
                    >
                      <i className='bi bi-plus-lg senderid-menu-action__icon'></i>
                      <p className='senderid-menu-action__label'>
                        {t('SENDER_ID.CREATE_NEW_CUSTOM_BUSINESS_NAME')}
                      </p>
                    </div>
                  </OverlayTrigger>
                )}
                {[REGION.AU, REGION.US].includes(userRegion) && (
                  <OverlayTrigger
                    placement='top'
                    container={typeof window !== 'undefined' ? document.body : undefined}
                    containerPadding={20}
                    overlay={
                      <Tooltip id='button-tooltip' className='high-z-index-tooltip'>
                        {t('SENDER_ID.CREATE_NEW_CUSTOM_NUMBER')}
                      </Tooltip>
                    }
                  >
                    <div
                      className={clsx('senderid-menu-action', {
                        'd-none': !isDefaultRegion,
                      })}
                      onClick={() => {
                        setCustomNumber(true)
                      }}
                    >
                      <i className='bi bi-plus-lg senderid-menu-action__icon'></i>
                      <p className='senderid-menu-action__label'>
                        {t('SENDER_ID.CREATE_NEW_CUSTOM_NUMBER')}
                      </p>
                    </div>
                  </OverlayTrigger>
                )}
              </>
            )}
          </>
        ) : null}
      </components.MenuList>
    )
  }

  return (
    <div
      className={`wizard-step setting-form-connect ${
        isExpanded ? 'border-primary' : ''
      } border bg-white`}
    >
      <div
        className={`wizard-step-header cursor-pointer ${
          isContentVisible ? 'border-radius-bottom-0' : 'border-radius-bottom'
        }`}
        onClick={toggleContent}
      >
        <div className='step-indicator'>
          <div className={`step-number ${isCompleted && !(gateUntilOpened && !hasOpenedOnce) ? 'completed' : 'border-secondary'} `}>
            <span></span>
            {isCompleted && !(gateUntilOpened && !hasOpenedOnce) && (
              <svg
                className='check-icon'
                width='12'
                height='9'
                viewBox='0 0 12 9'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M10.6668 1L4.00016 7.66667L1.3335 5'
                  stroke='currentColor'
                  strokeWidth='1.67'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </div>
          <div className='step-title d-flex flex-column'>
            <h2 className={`${isExpanded ? 'text-primary' : 'text-dark'} fw-semibold fs-2`}>
              {t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.TITLE')}
            </h2>
            <span>{t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.SUBTITLE')}</span>
          </div>
          <h2 className={`${isExpanded ? 'text-primary' : 'text-muted'} fw-semibold fs-2 text-number-step`}>4</h2>
          <div className={`collapse-arrow ${!isContentVisible ? 'collapsed' : ''}`}>
            <svg
              width='10'
              height='6'
              viewBox='0 0 10 6'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M1 1L5 5L9 1'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>
      </div>

      <div className={`wizard-step-content ${!isContentVisible ? 'd-none' : 'p-4'}`}>
        <div className='setting-form-connect-content'>
          {/* Promo Actions Section */}
          <Form.Group className='mb-4'>
            <Form.Label className='fw-bold d-flex align-items-center'>
              {t('WALLET_CARD.FORM.SHARE_OPTIONS.PROMO_ACTION')}
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip id='tooltip'>
                    {t('MOBILE_WALLET_CARD.FORM.PROMO_ACTION_TOOLTIP')}
                  </Tooltip>
                }
              >
                <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1 mt-1'></i>
              </OverlayTrigger>
            </Form.Label>
            <InputPromoAction
              value={promoSettingIds || []}
              onChange={handlePromoActionChange}
              promoActionList={promoActionList}
              className='react-tags-selectbox'
              name='promoSettings'
            />
          </Form.Group>

          {/* Vehicle Types for this Card (template restriction) */}
          <Form.Group className='mb-4'>
            <Form.Label className='fw-bold d-flex align-items-center'>
              {t('WALLET_CARD.FORM.VEHICLE_TYPES_FOR_CARD.TITLE') || 'Vehicle Types for this Card'}
            </Form.Label>
            <InputVehicleTypeOption
              value={templateVehicleTypeSelection}
              onChange={(value) => {
                setTemplateVehicleTypeSelection(value)
                if (setVehicleTypeOptionIds) setVehicleTypeOptionIds(value)
                handleChange({
                  target: {
                    name: 'vehicleTypeOptions',
                    value,
                  },
                })
              }}
              className='react-tags-selectbox'
              name='vehicleTypeOptionsTemplate'
              allowCreate={true}
            />
          </Form.Group>

          <Form.Group className='mb-4'>
            <Row>
              <Col md={4}>
                <Form.Label>{t('WALLET_CARD.FORM.BACKGROUND_COLOR')}</Form.Label>
                <div className='color-picker-group'>
                  <div className='color-preview-box-wrapper'>
                    <div
                      className='color-preview-box'
                      style={{background: cardData.formBackgroundColor}}
                    />
                    <input
                      id='colorPickerInput'
                      type='color'
                      name='formBackgroundColor'
                      value={cardData.formBackgroundColor}
                      onChange={handleChange}
                      disabled={disableInput}
                      className='color-picker-input'
                      tabIndex={-1}
                      aria-label='Choose color'
                    />
                  </div>
                  <input
                    type='text'
                    name='formBackgroundColor'
                    value={cardData.formBackgroundColor}
                    onChange={handleChange}
                    maxLength={30}
                    className='color-hex-input input-card-information'
                    disabled={disableInput}
                  />
                </div>
              </Col>
              <Col md={4}>
                <Form.Label>{t('WALLET_CARD.FORM.LABEL_COLOR')}</Form.Label>
                <div className='color-picker-group'>
                  <div className='color-preview-box-wrapper'>
                    <div
                      className='color-preview-box'
                      style={{background: cardData.formLabelColor}}
                    />
                    <input
                      id='labelColorPickerInput'
                      type='color'
                      name='formLabelColor'
                      value={cardData.formLabelColor || '#000000'}
                      onChange={handleChange}
                      disabled={disableInput}
                      className='color-picker-input'
                      tabIndex={-1}
                      aria-label='Choose text color'
                    />
                  </div>
                  <input
                    type='text'
                    name='formLabelColor'
                    value={cardData.formLabelColor}
                    onChange={handleChange}
                    maxLength={30}
                    className='color-hex-input input-card-information'
                    disabled={disableInput}
                  />
                </div>
              </Col>
            </Row>
          </Form.Group>
          {/* Form Fields Section */}
          <Form.Group className='mb-8'>
            <Form.Label className='fw-bold d-flex align-items-center'>
              {t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.FORM_FIELDS_CONFIGURATION')}
              <span className='required-field text-danger'>*</span>
            </Form.Label>
            <ReactSortable
              list={sortableFields}
              setList={handleSortableChange}
              animation={200}
              disabled={isLoading}
              ghostClass='sortable-ghost'
              chosenClass='sortable-chosen'
              dragClass='sortable-drag'
              handle='.drag-handle'
            >
              {sortableFields.map((field, index) => (
                <div
                  key={field._id}
                  className={`mb-4 w-100 sortable-item ${
                    field.isDisplay === false ? 'opacity-50' : ''
                  }`}
                >
                  <div className='d-flex align-items-center justify-content-start gap-3 flex-row w-100'>
                    <OverlayTrigger
                      placement='top'
                      overlay={
                        <Tooltip>
                          {t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.DRAG_TO_REORDER')}
                        </Tooltip>
                      }
                    >
                      <div className='drag-handle d-flex align-items-center justify-content-center'>
                        <i className='bi bi-grip-vertical drag-handle'></i>
                      </div>
                    </OverlayTrigger>
                    <div className='w-100'>
                      <Form.Control
                        type='text'
                        value={field.label}
                        className='form-control input-setting-form-connect'
                        disabled={true}
                        readOnly
                      />
                    </div>
                    <div>
                      <Form.Select
                        value={field.isRequired ? REQUIRED_VALUE : OPTIONAL_VALUE}
                        onChange={(e) =>
                          updateFormField(
                            field._id,
                            'isRequired',
                            e.target.value === REQUIRED_VALUE
                          )
                        }
                        className='form-control-sm select-requirement'
                        disabled={
                          isLoading || field.isDisplay === false || field.label === 'Full Name'
                        }
                      >
                        <option value={REQUIRED_VALUE}>
                          {t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.REQUIRED')}
                        </option>
                        <option value={OPTIONAL_VALUE}>
                          {t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.OPTIONAL')}
                        </option>
                      </Form.Select>
                    </div>
                    <div className='text-center field-visibility-checkbox'>
                      <OverlayTrigger
                        placement='left'
                        overlay={
                          <Tooltip>
                            {field.label === 'Full Name'
                              ? t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.FULL_NAME_REQUIRED')
                              : field.isDisplay === false
                              ? t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.ENABLE_FIELD')
                              : formFields.filter((f) => f.isDisplay !== false).length ===
                                MIN_FIELDS_ALLOWED
                              ? t(
                                  'WALLET_CARD.FORM.SETTING_FORM_CONNECT.NUMBER_FIELDS_REQUIRED_TOOLTIP',
                                  {
                                    number: MIN_FIELDS_ALLOWED,
                                  }
                                )
                              : t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.DISABLE_FIELD')}
                          </Tooltip>
                        }
                      >
                        <div className='checkbox-wrapper'>
                          <Form.Check
                            type='checkbox'
                            checked={field.isDisplay !== false}
                            onChange={() => toggleFieldVisibility(field._id)}
                            disabled={
                              field.label === 'Full Name' ||
                              (field.isDisplay !== false &&
                                formFields.filter((f) => f.isDisplay !== false).length <=
                                  MIN_FIELDS_ALLOWED) ||
                              isLoading
                            }
                          />
                        </div>
                      </OverlayTrigger>
                    </div>
                  </div>
                </div>
              ))}
            </ReactSortable>
          </Form.Group>
          <h3 className='fw-bold form-label fs-2'>
            {t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.TITLE_SETUP_WELCOME_MESSAGE')}
          </h3>

          {/* SMS Template */}

          <Form.Group className='mb-4'>
            <Form.Label className='fw-bold d-flex align-items-center'>
              {t('WALLET_CARD.FORM.SHARE_OPTIONS.SMS_TEMPLATE')}
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip id='sms-template-tooltip'>
                    {t('WALLET_CARD.FORM.SHARE_OPTIONS.SMS_TEMPLATE_TOOLTIP') || 'Select an SMS template for your wallet card sharing'}
                  </Tooltip>
                }
              >
                <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1 mt-1'></i>
              </OverlayTrigger>
            </Form.Label>
            <div className='d-flex align-items-center gap-2'>
              <div className='flex-grow-1'>
                <Select
                  value={selectedTemplate}
                  components={{MenuList}}
                  isSearchable={true}
                  isDisabled={isLoading}
                  placeholder={t('WALLET_CARD.FORM.SELECT_TEMPLATE') || 'Select Template'}
                  className='react-tags-selectbox'
                  options={templateOptions}
                  onChange={handleTemplateChange}
                  name='template'
                  menuPosition='fixed'
                  menuPlacement='top'
                  styles={selectStyles}
                />
              </div>
              <div className='d-flex align-items-center gap-2'>
                <Select
                  value={selectedDelay}
                  isSearchable={false}
                  isClearable={false}
                  isDisabled={isLoading}
                  placeholder='min'
                  className='react-delay-selectbox'
                  components={{IndicatorSeparator: () => null}}
                  options={delayOptions}
                  onChange={handleDelayChange}
                  name='delayMinutes'
                  menuPosition='fixed'
                  menuPlacement='top'
                  styles={delaySelectStyles}
                />
              </div>
            </div>
          </Form.Group>
          {/* Sender ID */}
          <Form.Group className='mb-4'>
            <Form.Label className='fw-bold d-flex align-items-center'>
              {t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.SENDER_ID')}
            </Form.Label>
            <div>
              <StripeJSProvider>
                {({stripe}: any) =>
                  stripe ? (
                    <Elements stripe={stripe}>
                      <VirtualNumbersModal
                        show={visible}
                        handleClose={() => {
                          setVisible(false)
                        }}
                      />
                    </Elements>
                  ) : null
                }
              </StripeJSProvider>
              <CreateBusinessNamePopup show={name} handleClose={() => setName(false)} />
              <CreateCutomNumber show={customNumber} handleClose={() => setCustomNumber(false)} />

              <Select
                components={{
                  MenuList: (props) => <MenuListComponent isVisible={true} {...props} />,
                }}
                onChange={(e: SelectOption<string>) => {
                  const senderType = findMatchingArrayName(e?.value, senderTypeData)
                  if (alphaID.includes(senderType)) {
                    handleSettingSenderChange(e)
                  } else {
                    handleSettingSenderChange(e)
                  }
                }}
                value={
                  selectedSender
                    ? {value: selectedSender, label: selectedSender}
                    : {
                        value: [REGION.AU, REGION.US].includes(userRegion) ? '#SharedNum#' : '',
                        label: [REGION.AU, REGION.US].includes(userRegion)
                          ? '#SharedNum#'
                          : 'Select Sender',
                      }
                }
                options={[
                  ...([REGION.AU, REGION.US].includes(userRegion)
                    ? [{value: '#SharedNum#', label: '#SharedNum#'}]
                    : []),
                  ...([REGION.AU, REGION.US].includes(userRegion) ? dedicatedOptionSMS : []),
                  ...([REGION.AU, REGION.US].includes(userRegion) ? dedicatedOptionMMS : []),
                  ...([REGION.AU, REGION.US, REGION.UK].includes(userRegion)
                    ? businessNameOption
                    : []),
                  ...([REGION.AU, REGION.US].includes(userRegion) ? customNumberOption : []),
                ]}
                data-testid='ddSenderID'
                isOptionDisabled={(option: any, value: any) =>
                  option?.disabled ? option?.disabled : false
                }
                menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
                menuPosition='fixed'
                menuPlacement='top'
                styles={selectStyles}
                name='sender'
                className='react-tags-selectbox'
              />
            </div>
          </Form.Group>
          <Modal
            show={openTemplatePopup}
            onHide={() => setOpenTemplatePopup(false)}
            size='xl'
            centered
            keyboard={true}
          >
            <Modal.Body>
              <div
                className='sms-node-modal-body'
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
              >
                <CreateScratchV2
                  onSave={handleModalSave}
                  shouldDirectAfterSave={false}
                  isEdit={false}
                  id={undefined}
                  templateData={undefined}
                />
              </div>
            </Modal.Body>
          </Modal>
        </div>
      </div>
    </div>
  )
}

export default SettingFormConnect
